import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { api, apiError } from "@/services/api"

interface OrgUnitMoveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  unit: { id: string; name: string } | null
  /** Flache Liste aller Einheiten fuer die Zielauswahl (inkl. Ebene zur Einrueckung). */
  flatUnits: { id: string; name: string; level: number }[]
  /** IDs, die als Ziel gesperrt sind (die Einheit selbst + alle Nachfahren). */
  disabledIds: Set<string>
}

/** Dialog "Verschieben": neue Übereinheit (oder Wurzel) für einen Organigramm-Knoten wählen. */
export function OrgUnitMoveDialog({
  open,
  onOpenChange,
  unit,
  flatUnits,
  disabledIds,
}: OrgUnitMoveDialogProps) {
  const queryClient = useQueryClient()
  const [targetId, setTargetId] = useState<string>("")

  const mutation = useMutation({
    mutationFn: () =>
      api.organization.orgUnits.update(unit!.id, {
        parentId: targetId ? Number(targetId) : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-chart"] })
      queryClient.invalidateQueries({ queryKey: ["org-units"] })
      toast.success("Einheit verschoben")
      onOpenChange(false)
      setTargetId("")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) setTargetId("")
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Einheit verschieben</DialogTitle>
          <DialogDescription>
            Neue Übereinheit für „{unit?.name}“ wählen. Die Einheit selbst und ihre Unterknoten
            stehen nicht zur Auswahl (kein Zyklus möglich).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label htmlFor="org-unit-target">Neue Übereinheit</Label>
          <Select
            id="org-unit-target"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
          >
            <option value="">— Wurzel (keine Übereinheit) —</option>
            {flatUnits
              .filter((u) => !disabledIds.has(u.id))
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {"  ".repeat(u.level - 1)}
                  {u.name}
                </option>
              ))}
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            Verschieben
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
